import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import {
  API_URL,
  CMR_AMBALAJE_LIST,
  CMR_DESCARCARI_LIST,
  TRAILERS_L,
  TRUCKS_L,
  formatName,
  isExpiredJWT,
  revertName,
  rutaFilter,
} from "../utils";
import {
  Button,
  Checkbox,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import useFeedback from "../hooks/useFeedback";
import CustomError from "./CustomError";
import useRute from "../hooks/useRute";
import { v4 as uuidv4 } from "uuid";

let cfgUsers = [];

function CreateCmr() {
  const { auth, refresh } = useContext(AuthContext);
  const isAdmin = auth.user.permissions.includes("Admin");
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rute = useRute();
  const ruteArr = isAdmin ? rute.map((ruta) => ruta.number) : rute;
  const setFeedback = useFeedback();
  const navigate = useNavigate();

  const errorCfg = {
    added_by_user: {
      func: (v) => cfgUsers.find((user) => user.name === revertName(v)),
      msg: "Nume utilizator invalid",
    },
    truck: { func: (v) => TRUCKS_L.includes(v), msg: "Numar camion invalid" },

    trailer: {
      func: (v) => TRAILERS_L.includes(v),
      msg: "Numar remorca invalid",
    },
    ambalaje: {
      func: (v) => CMR_AMBALAJE_LIST.includes(v),
      msg: "Valoare ambalaje invalida",
    },
    locul_descarcarii: {
      func: (v) => CMR_DESCARCARI_LIST.includes(v),
      msg: "Loc descarcare invalid",
    },
    destinatar: {
      func1: (arr) => arr.every((val) => ruteArr.includes(val)),
      msg1: "Valoare destinatar invalida",
      func2: (arr) => arr.length > 0,
      msg2: "Campul este obligatoriu",
    },
    greutate: { func: (v) => v > 0, msg: "Numar greutate invalid" },
    numar_paleti: { func: (v) => v > 0, msg: "Numar paleti invalid" },
    documente: { func: (v) => v > 0, msg: "Numar TLB invalid" },
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: "",
      added_by_user: "",
      destinatar: [],
      locul_descarcarii: "",
      temperatura_controlata: false,
      documente: null,
      ambalaje: "",
      numar_paleti: null,
      greutate: null,
      instructiuni: "",
      truck: "",
      trailer: "",
    },
  });

  // GET USERS
  useEffect(() => {
    async function getUsers() {
      try {
        const res = await axios.get(
          API_URL + "/api/user/?active=true&normal=l",
          {
            headers: { Authorization: `Bearer ${auth.aT}` },
          }
        );
        cfgUsers = res.data;
        setUsers(res.data);
      } catch (err) {
        console.error(err.response);
        setErr(err);
      }
    }

    if (isAdmin) {
      if (isExpiredJWT(auth.user.exp)) refresh();
      else getUsers();
    } else setUsers(auth.user);
  }, [auth, refresh, isAdmin]);

  async function onSubmit(data) {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const requestId = uuidv4();

    data.date = new Date().toISOString();
    const user = isAdmin
      ? users.find((user) => user.name === revertName(data.added_by_user))
      : auth.user;
    data.added_by_user = user._id;
    data.destinatar = "LIDL DISCOUNT FILIALA " + data.destinatar.join(", ");
    data.documente = "TLB " + String(data.documente);

    const aT = isExpiredJWT(auth.user.exp) ? await refresh() : auth.aT;
    try {
      await axios.post(API_URL + `/api/l/cmr/create`, data, {
        headers: { Authorization: `Bearer ${aT}`, "X-Request-ID": requestId },
      });
      setFeedback({
        type: "Success",
        active: true,
        msg: "CMR-ul a fost creat cu succes.",
      });
      navigate("/");
    } catch (err) {
      console.error(err);
      setFeedback({
        type: "Error",
        active: true,
        msg: "CMR-ul nu a putut fi creat.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function formatNames() {
    return users.map((user) => formatName(user.name));
  }

  return err ? (
    <CustomError error={err} />
  ) : (
    <div>
      <h1 className="text-2xl font-bold mb-4">Creeaza CMR</h1>
      {users && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="max-w-[400px] flex flex-col gap-2"
        >
          {isAdmin && (
            <>
              <Controller
                name="added_by_user"
                control={control}
                rules={{
                  validate: (v) =>
                    errorCfg.added_by_user.func(v) ||
                    errorCfg.added_by_user.msg,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Utilizator"
                    withAsterisk
                    placeholder="Selecteaza utilizator..."
                    nothingFoundMessage="Utilizator inexistent"
                    searchable
                    data={formatNames()}
                  />
                )}
              />
            </>
          )}
          <Controller
            name="truck"
            control={control}
            rules={{
              validate: (val) => errorCfg.truck.func(val) || errorCfg.truck.msg,
            }}
            render={({ field }) => (
              <Select
                {...field}
                label="Camion"
                placeholder="Selecteaza un camion..."
                nothingFoundMessage="Camion inexistent"
                withAsterisk
                searchable
                data={TRUCKS_L}
              />
            )}
          />
          <Controller
            name="trailer"
            control={control}
            rules={{
              validate: (val) =>
                errorCfg.trailer.func(val) || errorCfg.trailer.msg,
            }}
            render={({ field }) => (
              <Select
                {...field}
                label="Remorca"
                placeholder="Selecteaza o remorca..."
                nothingFoundMessage="Remorca inexistenta"
                withAsterisk
                searchable
                data={TRAILERS_L}
              />
            )}
          />
          <Controller
            name="locul_descarcarii"
            control={control}
            rules={{
              validate: (val) =>
                errorCfg.locul_descarcarii.func(val) ||
                errorCfg.locul_descarcarii.msg,
            }}
            render={({ field }) => (
              <Select
                {...field}
                label="Locul descarcarii"
                placeholder="Selecteaza locul descarcarii..."
                nothingFoundMessage="Loc descarcare invalid"
                withAsterisk
                searchable
                data={CMR_DESCARCARI_LIST}
              />
            )}
          />
          <Controller
            name="destinatar"
            control={control}
            rules={{
              validate: {
                validValue: (val) =>
                  errorCfg.destinatar.func1(val) || errorCfg.destinatar.msg1,
                notEmpty: (val) =>
                  errorCfg.destinatar.func2(val) || errorCfg.destinatar.msg2,
              },
            }}
            render={({ field }) => (
              <MultiSelect
                {...field}
                label="Destinatar"
                error={errors.destinatar ? errors.destinatar.message : ""}
                placeholder="Adauga destinatar..."
                data={ruteArr}
                searchable
                filter={rutaFilter}
                required
                clearable
                nothingFoundMessage="Destinatar invalid..."
              />
            )}
          />
          <Controller
            name="ambalaje"
            control={control}
            rules={{
              validate: (v) =>
                errorCfg.ambalaje.func(v) || errorCfg.ambalaje.msg,
            }}
            render={({ field }) => (
              <Select
                {...field}
                label="Marfuri"
                placeholder="Selecteaza ambalaje..."
                nothingFoundMessage="Valoare inexistenta"
                withAsterisk
                searchable
                data={CMR_AMBALAJE_LIST}
              />
            )}
          />
          <Controller
            name="greutate"
            control={control}
            rules={{
              validate: (v) =>
                errorCfg.greutate.func(v) || errorCfg.greutate.msg,
            }}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Greutate"
                error={errors.greutate ? errors.greutate.message : ""}
                placeholder="Greutate..."
                min={1}
                decimalScale={2}
                withAsterisk
              />
            )}
          />
          <Controller
            name="numar_paleti"
            control={control}
            rules={{
              validate: (v) =>
                errorCfg.numar_paleti.func(v) || errorCfg.numar_paleti.msg,
            }}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="Numar paleti"
                error={errors.numar_paleti ? errors.numar_paleti.message : ""}
                placeholder="Numar paleti..."
                min={1}
                withAsterisk
              />
            )}
          />
          <Controller
            name="documente"
            control={control}
            rules={{
              validate: (v) =>
                errorCfg.documente.func(v) || errorCfg.documente.msg,
            }}
            render={({ field }) => (
              <NumberInput
                {...field}
                label="TLB"
                error={errors.documente ? errors.documente.message : ""}
                placeholder="Numar TLB..."
                min={1}
                withAsterisk
              />
            )}
          />
          <Controller
            name="instructiuni"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Instructiuni"
                placeholder="Instructiuni..."
              />
            )}
          />

          <Controller
            name="temperatura_controlata"
            control={control}
            render={({ field }) => (
              <Checkbox {...field} label="Temperatura controlata" />
            )}
          />

          <Button
            type="submit"
            className="!w-[100px] mt-2"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
          >
            Trimite
          </Button>
        </form>
      )}
    </div>
  );
}

export default CreateCmr;
